/* eslint-disable no-restricted-imports */
import * as React from 'react';

import { Heading, Text } from '@/components/ui';
import { WM_FLAT_SURFACE_CLASSNAME } from '@/components/ui/surfaceStyles';

const CHART_COLORS = [
    { name: 'chart-1', cssVar: '--chart-1', hex: '#25787E' },
    { name: 'chart-2', cssVar: '--chart-2', hex: '#80BC2E' },
    { name: 'chart-3', cssVar: '--chart-3', hex: '#F59D24' },
    { name: 'chart-4', cssVar: '--chart-4', hex: '#6EB3E3' },
    { name: 'chart-5', cssVar: '--chart-5', hex: '#C974AD' },
    { name: 'chart-6', cssVar: '--chart-6', hex: '#E94547' },
    { name: 'chart-7', cssVar: '--chart-7', hex: '#4D7D9E' },
    { name: 'chart-8', cssVar: '--chart-8', hex: '#797979' }
];

// Sample series — monotonic-ish values so the preview lines/bars vary
// enough to read each colour in isolation and against its neighbours.
const SAMPLE_SERIES = [
    [20, 28, 32, 30, 36, 42, 48, 52, 60, 65],
    [15, 18, 22, 26, 32, 34, 38, 42, 46, 52],
    [35, 30, 32, 28, 30, 26, 28, 32, 36, 40],
    [10, 14, 20, 24, 22, 28, 32, 36, 40, 44],
    [45, 42, 38, 36, 34, 30, 26, 22, 20, 18],
    [25, 28, 26, 30, 34, 38, 36, 40, 44, 48],
    [30, 34, 32, 36, 40, 38, 42, 44, 46, 50],
    [5, 8, 12, 18, 22, 26, 28, 32, 36, 40]
];

const SvgViewport = { width: 640, height: 180, padding: 16 };

const pointsFor = (series, max) => {
    const step = (SvgViewport.width - SvgViewport.padding * 2) / (series.length - 1);
    const usable = SvgViewport.height - SvgViewport.padding * 2;

    return series
        .map((value, index) => {
            const x = SvgViewport.padding + step * index;
            const y = SvgViewport.padding + usable - (value / max) * usable;

            return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(' ');
};

const MultiLinePreview = () => {
    const max = Math.max(...SAMPLE_SERIES.flat());

    return (
        <svg
            aria-hidden="true"
            className="tw-block tw-h-auto tw-w-full"
            preserveAspectRatio="none"
            viewBox={`0 0 ${SvgViewport.width} ${SvgViewport.height}`}
        >
            {SAMPLE_SERIES.map((series, index) => (
                <polyline
                    key={index}
                    fill="none"
                    points={pointsFor(series, max)}
                    stroke={`var(${CHART_COLORS[index].cssVar})`}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                />
            ))}
        </svg>
    );
};

const StackedBarsPreview = () => {
    const groupCount = 6;
    const groupWidth = SvgViewport.width / groupCount;
    const barWidth = groupWidth * 0.65;
    const barGap = (groupWidth - barWidth) / 2;
    const usable = SvgViewport.height - SvgViewport.padding * 2;

    const totals = Array.from({ length: groupCount }, (_, gi) => (
        CHART_COLORS.reduce((acc, _color, ci) => acc + SAMPLE_SERIES[ci][gi], 0)
    ));
    const maxTotal = Math.max(...totals);

    return (
        <svg
            aria-hidden="true"
            className="tw-block tw-h-auto tw-w-full"
            preserveAspectRatio="none"
            viewBox={`0 0 ${SvgViewport.width} ${SvgViewport.height}`}
        >
            {Array.from({ length: groupCount }, (_, gi) => {
                let cumulative = 0;

                return CHART_COLORS.map((color, ci) => {
                    const value = SAMPLE_SERIES[ci][gi];
                    const height = (value / maxTotal) * usable;
                    const y = SvgViewport.padding + usable - cumulative - height;

                    cumulative += height;

                    return (
                        <rect
                            key={`${gi}-${ci}`}
                            fill={`var(${color.cssVar})`}
                            height={height}
                            width={barWidth}
                            x={gi * groupWidth + barGap}
                            y={y}
                        />
                    );
                });
            })}
        </svg>
    );
};

const DonutPreview = () => {
    const radius = 70;
    const cx = SvgViewport.width / 2;
    const cy = SvgViewport.height / 2;
    const circumference = 2 * Math.PI * radius;

    const values = CHART_COLORS.map((_, ci) => SAMPLE_SERIES[ci][0]);
    const total = values.reduce((acc, v) => acc + v, 0);

    let offset = 0;

    return (
        <svg
            aria-hidden="true"
            className="tw-block tw-h-auto tw-w-full"
            viewBox={`0 0 ${SvgViewport.width} ${SvgViewport.height}`}
        >
            <g transform={`rotate(-90 ${cx} ${cy})`}>
                {CHART_COLORS.map((color, ci) => {
                    const share = values[ci] / total;
                    const length = share * circumference;
                    const circle = (
                        <circle
                            key={ci}
                            cx={cx}
                            cy={cy}
                            fill="none"
                            r={radius}
                            stroke={`var(${color.cssVar})`}
                            strokeDasharray={`${length} ${circumference - length}`}
                            strokeDashoffset={-offset}
                            strokeWidth={28}
                        />
                    );

                    offset += length;

                    return circle;
                })}
            </g>
        </svg>
    );
};

const Swatch = ({ token }) => (
    <div className={`${WM_FLAT_SURFACE_CLASSNAME} tw-overflow-hidden tw-rounded-2xl`}>
        <div
            className="tw-h-20"
            style={{ backgroundColor: `var(${token.cssVar})` }}
        />
        <div className="tw-space-y-1 tw-p-4">
            <Text as="p" variant="control">{token.name}</Text>
            <Text as="p" tone="muted" variant="caption"><code>{token.hex}</code></Text>
            <Text as="p" tone="muted" variant="caption"><code>var({token.cssVar})</code></Text>
        </div>
    </div>
);

export default function ChartsFoundation() {
    return (
        <div className="tw-space-y-8">
            <header className="tw-space-y-2">
                <Text tone="muted" variant="overline">Foundation</Text>
                <Heading as="h2" variant="title-lg">Charts</Heading>
                <Text as="p" tone="muted" variant="body">
                    An eight-colour sequence used exclusively for chart series. The palette
                    is theme-independent — series should stay recognisable whether the user
                    is in light or dark mode.
                </Text>
            </header>

            <section className={`${WM_FLAT_SURFACE_CLASSNAME} tw-rounded-3xl tw-p-6 lg:tw-p-8`}>
                <Heading as="h3" variant="title">Palette</Heading>
                <div className="tw-mt-4 tw-grid tw-grid-cols-2 tw-gap-4 sm:tw-grid-cols-4 lg:tw-grid-cols-8">
                    {CHART_COLORS.map(token => (
                        <Swatch key={token.name} token={token} />
                    ))}
                </div>
            </section>

            <section className={`${WM_FLAT_SURFACE_CLASSNAME} tw-rounded-3xl tw-p-6 lg:tw-p-8`}>
                <Heading as="h3" variant="title">Line series</Heading>
                <Text tone="muted" variant="body-sm">
                    Eight overlapping series rendered in sequence order.
                </Text>
                <div className="tw-mt-4">
                    <MultiLinePreview />
                </div>
            </section>

            <section className={`${WM_FLAT_SURFACE_CLASSNAME} tw-rounded-3xl tw-p-6 lg:tw-p-8`}>
                <Heading as="h3" variant="title">Stacked bars</Heading>
                <Text tone="muted" variant="body-sm">
                    Adjacent colours should remain distinguishable when stacked.
                </Text>
                <div className="tw-mt-4">
                    <StackedBarsPreview />
                </div>
            </section>

            <section className={`${WM_FLAT_SURFACE_CLASSNAME} tw-rounded-3xl tw-p-6 lg:tw-p-8`}>
                <Heading as="h3" variant="title">Donut</Heading>
                <Text tone="muted" variant="body-sm">
                    Useful for spotting hue drift when series sit next to one another.
                </Text>
                <div className="tw-mt-4">
                    <DonutPreview />
                </div>
            </section>
        </div>
    );
}
